import { stringify } from "querystringify"

import { CURRENCY } from "utils/constants"
import { BookingStatus, SlotType } from "utils/interfaces"
import { SimplifiedCall } from "modules/Call/queries"
import { httpClient } from "utils/infrastructure/httpClient"

export interface Pagination<Result> {
    count: number
    next: string | null
    previous: string | null
    results: Result[]
}

export interface BookingPsy {
    name: string
    title: string
    face_url: string
    phone_number: string
    hash_id: string
    public_profile_url: string
    videotelephony: string
    addresses: string
    city: string
    location_details: string
    available_for_booking: Record<SlotType, string | null>
    experimental_group_member: boolean
    alter_video_call: boolean
}

interface BookingServiceType {
    id: number
    name: string
    online: boolean
    family: boolean
    description: string
    public: boolean
}

export interface CancellationInfo {
    datetime: string
    initiator: "client" | "psychologist" | "system" | "admin"
}

export interface Booking {
    psy: BookingPsy
    session_start_datetime: string
    session_end_datetime: string
    previous_session_start_datetime: string | null
    session_start_datetime_changed_datetime: string | null
    price: number
    service_type: BookingServiceType
    booking_hash: string
    status: BookingStatus
    created_by_psychologist: boolean
    is_refund_possible: boolean
    cancellation_info: CancellationInfo | null
    currency: CURRENCY
    has_fixed_session_time: boolean
    call?: SimplifiedCall
    is_booking_available_for_call: boolean
}

export interface SelectionsResponse {
    id: number
    timestamp: string
    results_link: string
    face_urls: string[]
    top_competence?: string
    subject: string
    reported_problems: string[]
}

export interface Calendar {
    share_link: string
}

export interface RecommendedPsy {
    display_name: string
    hash_id: string
    public_profile_url: string
    avatar_url: string
    title: string
}

export interface PaymentMethod {
    card_mask: string
    card_type: string
    external_id: string
    payment_system: number
}

export interface Ref {
    url: string
}

export enum REF_ERRORS {
    referral_code_expired = "referral_code_expired",
    no_referral_code = "no_referral_code",
}

export interface RefErrorResponse {
    response: {
        error: REF_ERRORS
        fields: []
    }
}

/**
 * Client endpoints
 */

export function getSelections() {
    return httpClient.get<SelectionsResponse[]>("client/questionnaire-results")
}

export function getCalendar() {
    return httpClient.get<Calendar>("client/calendar")
}

export function createCalendarShareLink() {
    return httpClient.post<Calendar>("client/calendar")
}

export function getRecommendedPsy() {
    return httpClient.get("client/last-psychologist")
}

/**
 * BookingDetails endpoints
 */

export function getMostImportantBookings() {
    return httpClient.get<Booking[]>("booking/list/most-important")
}

export function getPastBookings(page = 1) {
    return httpClient.get<Pagination<Booking>>(`booking/list/past${stringify({ page }, "?")}`)
}

export function getCanceledBookings(page = 1) {
    return httpClient.get<Pagination<Booking>>(`booking/list/canceled${stringify({ page }, "?")}`)
}

export function getFutureBookings() {
    return httpClient.get<Booking[]>("booking/list/future")
}

/**
 * Payment endpoints
 */

export function getPaymentMethods() {
    return httpClient.get<PaymentMethod[]>("payment/client-tools")
}

export function deletePaymentMethods(externalId: string) {
    return httpClient.delete(`payment/client-tools/${externalId}`)
}

export function getRefShareLink() {
    return httpClient.get<Ref & RefErrorResponse>("client/referral/code")
}
export function createRefShareLink() {
    return httpClient.post<Ref>("client/referral/code")
}

export interface GetTelegramStartUrlResponseData {
    url: string
}

export function getTelegramStartUrl() {
    return httpClient.get<GetTelegramStartUrlResponseData>("client/telegram-start-url")
}
